import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import Global from '../../store/global-selectors';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { useGetValidConfirmationForService } from './use-get-valid-confirmation-for-service';
import { US } from '../../constants/country-codes';
import { markUserPackageEdit } from '../../orders/order-details/configure-shipment/fields/packing-recommend/use-packing-applied';

const returnNonNull = (first, second) => {
  return first == null ? second : first;
};

const overwriteExistingPackageWithNonNullValues = (existingPackage = {}, presetPackage = {}) => {
  return {
    ...existingPackage,
    dimensions: returnNonNull(presetPackage.dimensions, existingPackage.dimensions),
    packageTypeId: returnNonNull(presetPackage.packageTypeId, existingPackage.packageTypeId),
    packageId: returnNonNull(presetPackage.packageId, existingPackage.packageId),
    weight: returnNonNull(presetPackage.weight, existingPackage.weight),
  };
};

const determineInsuredValue = (itemValue, presetInsuranceProvider, sellerDefaultCurrencyCode) => {
  if (presetInsuranceProvider === InsuranceProviders.None.name) {
    return { value: 0, code: sellerDefaultCurrencyCode };
  }
  return { value: itemValue, code: sellerDefaultCurrencyCode };
};

export const useApplyPreset = (
  fulfillmentPlanId,
  onPresetWithInsuranceProviderApplied,
  onPackagesSave,
  onPresetWithServiceApplied,
  onShipFromChange,
  packages,
  onConfirmationChange,
  selectedConfirmationId,
  checkPackagesAfterServiceChange,
  serviceCollectionGuid,
) => {
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const sellerDefaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) || {};
  const isExternal = !!fulfillmentPlan?.sellerFulfillmentProviderId;
  const fpItems = fulfillmentPlan.items || [];
  const itemValue = fpItems.reduce((sum, i) => sum + (i?.totalPrice?.value ?? 0), 0);

  const getValidConfirmationForService = useGetValidConfirmationForService();

  const applyPresetConfiguration = useCallback(
    (payload = {}) => {
      const presetShipFrom = payload?.presetOptions?.shipFromId;
      if (presetShipFrom) {
        onShipFromChange(presetShipFrom);
      }

      if (!presetShipFrom && isExternal) {
        return;
      }

      const presetServiceId = payload?.presetOptions?.serviceId;

      const presetPackages = payload?.packages || [];
      const presetInsuranceProvider = payload?.presetOptions?.insuranceProvider;
      const presetPackageIsJustDimensions = presetPackages.length === 1 && !presetPackages[0].packageId;
      const serviceCollectionDoesNotBlockPackageUpdate =
        !serviceCollectionGuid || sellerHomeCountry === US || presetPackageIsJustDimensions;

      if ((presetPackages.length || presetInsuranceProvider) && serviceCollectionDoesNotBlockPackageUpdate) {
        markUserPackageEdit(fulfillmentPlanId);
        if (presetInsuranceProvider) {
          const [payloadPackage] = presetPackages;
          const [firstPackage] = packages;
          const updatedPackage = {
            ...overwriteExistingPackageWithNonNullValues(firstPackage, payloadPackage),
            insuredValue: determineInsuredValue(itemValue, presetInsuranceProvider, sellerDefaultCurrencyCode),
          };

          onPackagesSave([updatedPackage, ...packages.slice(1)]);
          onPresetWithInsuranceProviderApplied(presetInsuranceProvider);
        } else {
          const [presetPackage] = presetPackages;
          const [firstPackage] = packages;
          const updatedPackage = overwriteExistingPackageWithNonNullValues(firstPackage, presetPackage);
          onPackagesSave([updatedPackage, ...packages.slice(1)]);
        }
      } else if (presetServiceId) {
        checkPackagesAfterServiceChange(presetServiceId);
      }

      const validConfirmationId = getValidConfirmationForService(presetServiceId, selectedConfirmationId);
      const presetConfirmation = payload?.options?.confirmationEnumId;
      const hasConfirmationPresetWithoutService = !presetServiceId && presetConfirmation;
      if (hasConfirmationPresetWithoutService) {
        onConfirmationChange(presetConfirmation);
      }

      // check for service AFTER packages so that the csw
      // reducer has the up to date value before changeServiceAndOtherShippingOptions
      // is triggered
      const presetServiceCollectionGuid = payload?.presetOptions?.serviceCollectionGuid;
      if (presetServiceId || presetServiceCollectionGuid) {
        onPresetWithServiceApplied({
          serviceId: presetServiceId,
          sellerLabelProviderId: payload.presetOptions.sellerLabelProviderId,
          serviceCollectionGuid: presetServiceCollectionGuid,
          confirmationId: presetConfirmation || validConfirmationId,
        });
      }
    },
    [
      isExternal,
      itemValue,
      onConfirmationChange,
      onPresetWithInsuranceProviderApplied,
      onPackagesSave,
      onPresetWithServiceApplied,
      onShipFromChange,
      packages,
      sellerDefaultCurrencyCode,
      selectedConfirmationId,
      getValidConfirmationForService,
      checkPackagesAfterServiceChange,
      serviceCollectionGuid,
      sellerHomeCountry,
      fulfillmentPlanId,
    ],
  );
  return applyPresetConfiguration;
};
